import React, { useState } from 'react';
import { Box, Button, FormControl, MenuItem, Rating, Select, Stack, TextField, Typography } from '@mui/material';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { useSubmitFeedbackSurveyMutation } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { SubmitFeedbackSurveyRequest } from 'src/model/api/v2/data-contracts';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { getFrontendURL } from 'src/utils/url';
import { ts } from 'src/utils/translate';

type SurveyOption = { id: number, label: string };

const EMPTY_SURVEY: SubmitFeedbackSurveyRequest = {
    overall: 0,
    cancel_reason: 0,
    cancel_reason_text: '',
    useful_feature: 0,
    useful_feature_text: '',
    would_recommend: 0,
    improve_text: '',
};

// the last option of each select is "Other", which asks for a free text
const cancelReasonOptions = (): SurveyOption[] => [
    { id: 1, label: ts('feedback_survey_reason_no_longer_need', 'No longer need it.') },
    { id: 2, label: ts('feedback_survey_reason_not_meet_needs', "It didn't meet my needs.") },
    { id: 3, label: ts('feedback_survey_reason_ease_of_use', 'Ease of use was less than expected.') },
    { id: 4, label: ts('feedback_survey_other', 'Other - please specify:') },
];

const usefulFeatureOptions = (): SurveyOption[] => [
    { id: 1, label: ts('feedback_survey_feature_wizard', 'uPlan Wizard') },
    { id: 2, label: ts('feedback_survey_feature_quote', 'Auto-generated quote and documentation') },
    { id: 3, label: ts('feedback_survey_feature_cable_planning', 'Cable planning') },
    { id: 4, label: ts('feedback_survey_feature_loxone_config', 'Loxone Config export') },
    { id: 5, label: ts('feedback_survey_other', 'Other - please specify:') },
];

type Props = {
    open: boolean;
    onClose: () => void;
};

export const FeedbackSurveyDialog = ({ open, onClose }: Props) => {
    const dispatch = useAppDispatch();
    const [submitFeedbackSurvey, { isLoading }] = useSubmitFeedbackSurveyMutation();
    const [survey, setSurvey] = useState<SubmitFeedbackSurveyRequest>(EMPTY_SURVEY);

    const cancelReasons = cancelReasonOptions();
    const usefulFeatures = usefulFeatureOptions();

    const setAnswer = <F extends keyof SubmitFeedbackSurveyRequest>(field: F, value: SubmitFeedbackSurveyRequest[F]) => {
        setSurvey(prevSurvey => ({ ...prevSurvey, [field]: value }));
    };

    const handleClose = () => {
        setSurvey(EMPTY_SURVEY);
        onClose();
    };

    const isComplete = survey.overall > 0
        && survey.cancel_reason > 0
        && survey.useful_feature > 0
        && survey.would_recommend > 0;

    const handleSubmit = () => {
        submitFeedbackSurvey(survey)
            .unwrap()
            .catch((error) => {
                if (error?.data?.errorMessage == null) {
                    dispatch(showSnackBar({
                        text: ts('something_went_wrong_try_again', 'Something went wrong, please try again.'),
                        severity: 'error',
                    }));
                }
            })
            .finally(handleClose);
    };

    const renderQuestion = (text: string) => (
        <Typography variant="subtitle1" fontWeight="bold">{text}</Typography>
    );

    const renderStars = (field: 'overall' | 'would_recommend') => (
        <Rating
            size="large"
            value={survey[field] || null}
            onChange={(_event, value) => setAnswer(field, value ?? 0)}
        />
    );

    const renderSelectWithOther = (
        field: 'cancel_reason' | 'useful_feature',
        textField: 'cancel_reason_text' | 'useful_feature_text',
        options: SurveyOption[],
    ) => (
        <Stack spacing={1}>
            <FormControl fullWidth size="small">
                <Select
                    value={survey[field] || ''}
                    displayEmpty
                    onChange={(event) => setAnswer(field, Number(event.target.value))}
                >
                    <MenuItem value="" disabled sx={{ display: 'none' }}>
                        {ts('feedback_survey_please_select', 'Please select')}
                    </MenuItem>
                    {options.map(option => (
                        <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            {survey[field] === options[options.length - 1].id && (
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={survey[textField]}
                    onChange={(event) => setAnswer(textField, event.target.value)}
                />
            )}
        </Stack>
    );

    return (
        <BaseDialog
            open={open}
            onClose={handleClose}
            title={ts('feedback_survey_title', 'Feedback')}
            titleIcon={<Box component="img" src={`${getFrontendURL()}/images/logo_login_mobile_blue.svg`} sx={{ width: 120 }} />}
            actionsLeft={(
                <Button variant="contained" disabled={!isComplete || isLoading} onClick={handleSubmit}>
                    {ts('feedback_survey_submit', 'Submit')}
                </Button>
            )}
        >
            <Stack spacing={2}>
                {renderQuestion(ts('feedback_survey_overall', '1. On a scale of 1-5 how satisfied were you with the software overall?'))}
                {renderStars('overall')}
                {renderQuestion(ts('feedback_survey_cancel_reason', '2. What is the primary reason for cancelling your subscription?'))}
                {renderSelectWithOther('cancel_reason', 'cancel_reason_text', cancelReasons)}
                {renderQuestion(ts('feedback_survey_useful_feature', '3. Which features did you find the most useful?'))}
                {renderSelectWithOther('useful_feature', 'useful_feature_text', usefulFeatures)}
                {renderQuestion(ts('feedback_survey_would_recommend', '4. Would you recommend uPlan for others?'))}
                {renderStars('would_recommend')}
                {renderQuestion(ts('feedback_survey_improve', '5. What could we do to improve our software?'))}
                <TextField
                    multiline
                    rows={6}
                    fullWidth
                    value={survey.improve_text}
                    onChange={(event) => setAnswer('improve_text', event.target.value)}
                />
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                        {ts('feedback_survey_thanks_appreciate', 'We appreciate you taking the time to fill our feedback form.')}
                    </Typography>
                    <Typography variant="body2">{ts('feedback_survey_thanks', 'Thank you!')}</Typography>
                </Box>
            </Stack>
        </BaseDialog>
    );
};
